/**
 * waveMeta.js — single source of truth for how a tracking wave is labelled.
 *
 * Background: wave numbers are an internal detail of the tracker (`wave=N` in
 * the Parquet layout). Client-facing output wants the fieldwork month instead
 * ("Aug 2026"), and the mobile Trends tab has historically shown a quarter
 * label ("Q2 2025"). Both were hardcoded in different places, and the Parquet
 * `wave_date` column — the only machine-readable source — is NULL for waves 4
 * and 5, so it can't be relied on.
 *
 * Everything that needs to name a wave should read it from here.
 *
 * NOTE: waves 1–3 match the `wave_date` values in the Parquet files
 * (February 2025 / September 2025 / February 2026). Waves 4 and 5 have a NULL
 * `wave_date` and the months below are taken from the questionnaire dates —
 * confirm with the research team before these go in front of a client.
 */

const WAVES = {
  1: { date: "Feb 2025", dateLong: "February 2025", quarter: "Q1 2025" },
  2: { date: "Sep 2025", dateLong: "September 2025", quarter: "Q2 2025" },
  3: { date: "Feb 2026", dateLong: "February 2026", quarter: "Q1 2026" },
  4: { date: "Apr 2026", dateLong: "April 2026", quarter: "Q2 2026" },
  5: { date: "Jul 2026", dateLong: "July 2026", quarter: "Q3 2026" },
};

/** Short fieldwork label — "Aug 2026". Falls back to "Wave 5". */
export function waveDateLabel(n) {
  return WAVES[Number(n)]?.date || (n != null ? `Wave ${n}` : "");
}

/** Full fieldwork label — "August 2026". */
export function waveDateLabelLong(n) {
  return WAVES[Number(n)]?.dateLong || (n != null ? `Wave ${n}` : "");
}

/** Quarter label — "Q3 2026". Used by the mobile Trends tab. */
export function waveQuarterLabel(n) {
  return WAVES[Number(n)]?.quarter || (n != null ? `Wave ${n}` : "");
}

/** True when we have a real fieldwork month for this wave. */
export function hasWaveMeta(n) {
  return Boolean(WAVES[Number(n)]);
}

export default WAVES;
